<script setup lang="ts">
import { MapComponent } from '@apps/main/new-desktop/modules/maps';

import { AppButton } from '@libs/ui';
import { MapControl } from '@libs/maps';

import { useMapStore } from '../../core/stores/mapStore/mapStore';

import type { MapConfig } from './Maps.model';

interface DynamicFullScreenMapProps {
  mapConfiguration: MapConfig;
}
const mapStore = useMapStore();
const props = defineProps<DynamicFullScreenMapProps>();

interface DynamicFullScreenMapEmit {
  (event: 'close'): void;
}

const emit = defineEmits<DynamicFullScreenMapEmit>();
</script>

<template>
  <div class="dynamic-full-screen-map">
    <div class="dynamic-map-wrapper">
      <MapComponent
        :key="mapStore.mapTypeId"
        gesture-handling="auto"
        :show-all-markers="false"
        :markers="
          props.mapConfiguration.markers ? [props.mapConfiguration.markers] : []
        "
        :marker-formatters="props.mapConfiguration.markerFormatters"
        :start-rectangle="props.mapConfiguration.startRectangle"
        :map-controls="[MapControl.zoomControl, MapControl.mapTypeControl]"
        @on-map-type-id-changed="mapStore.setMapTypeId"
      />
      <AppButton
        class="close-button"
        type="secondary"
        state="enabled"
        size="medium"
        :aria-label="$t('a11y.minimise_map')"
        :icon="{ name: 'close' }"
        @click="emit('close')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.dynamic-full-screen-map {
  --map-spacing: var(--spacing-32);

  width: 100vw;
  height: 100dvh;
  height: 100vh;
  position: fixed;
  z-index: var(--panel-second-level-z-index);
  background-color: rgba(0, 0, 0, 0.9);
  @include for-more-or-equal-than-1200-px {
    padding: var(--map-spacing) 0;
  }

  .dynamic-map-wrapper {
    @include for-more-or-equal-than-1200-px {
      width: 70%;
      min-width: 960px;
      max-height: calc(100% - var(--map-spacing) * 2);
      aspect-ratio: 7/5;
      border-radius: var(--border-radius-12);
      overflow: hidden;
      margin: 0 auto;
    }
    position: relative;
    width: 100%;
    height: 100dvh;

    .close-button {
      position: absolute;
      top: 0;
      right: 0;
      margin: var(--spacing-24);
      box-shadow: var(--shadow);
      cursor: pointer;
    }
  }
}
</style>

<style lang="scss">
.dynamic-full-screen-map {
  --icon-size: 34px;

  @include map-zoom-control(var(--icon-size), 40px, -25px, -75px);
  @include map-type-switch(var(--spacing-24));

  .trip-planner-details-accommodation-marker,
  .trip-planner-details-activity-marker {
    height: var(--icon-size);
    width: var(--icon-size);
  }
}
</style>
